import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Bot, Plus, ExternalLink, Edit, Trash2, 
  MessageSquare, Calendar, Copy, Settings, Menu, X, LogOut, ChevronDown, CreditCard
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../translations';
import LanguageToggle from './LanguageToggle';
import { getChatbotUrl, getPublicChatbotUrl } from '../utils/urls';
import { API_URL } from '../utils/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const { user, logout } = useAuth();
  const { t } = useTranslation(language);
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success(t('navigation.logout'));
      navigate('/login');
      setIsProfileMenuOpen(false);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchChatbots();
  }, []);

  const fetchChatbots = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/chatbots`);
      setChatbots(response.data);
    } catch (error) {
      console.error('Error fetching chatbots:', error);
      toast.error('Failed to load chatbots');
    } finally {
      setLoading(false);
    }
  };

  const deleteChatbot = async (slug) => {
    try {
      await axios.delete(`${API_URL}/chatbots/${slug}`);
      toast.success('Chatbot deleted successfully');
      fetchChatbots();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting chatbot:', error);
      toast.error('Failed to delete chatbot');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Ultra-Glassy Header - Fixed to top */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/50 backdrop-blur-3xl border-b border-white/30 shadow-2xl shadow-gray-300/20" style={{backdropFilter: 'blur(40px) saturate(200%)', WebkitBackdropFilter: 'blur(40px) saturate(200%)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex justify-between items-center h-20">
            
            <Link to="/" className={`flex items-center gap-3 group ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="relative">
                <div className="absolute inset-0 bg-blue-600 rounded-xl blur-sm opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                {t('app.name')}
              </span>
            </Link>

            <nav className={`hidden md:flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <LanguageToggle />
              <Link 
                to="/" 
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 rounded-lg hover:bg-gray-100/50 transition-all"
              >
                خانه
              </Link>
              
              <div className="relative ml-2" ref={profileMenuRef}>
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className={`flex items-center gap-2 px-2 py-2 hover:bg-gray-100/50 rounded-lg transition-all ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center text-white text-sm font-semibold ring-2 ring-gray-200">
                    {user?.username?.charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isProfileMenuOpen && (
                  <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-3 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 overflow-hidden`}>
                    <div className="px-4 py-4 bg-gradient-to-br from-gray-50 to-white">
                      <p className="text-sm font-semibold text-gray-900">{user?.username}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{user?.email || 'User Account'}</p>
                    </div>
                    <div className="py-2">
                      <Link
                        to="/wallet"
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        <CreditCard className="w-4 h-4" />
                        <span>{t('wallet.title') || 'Wallet & Billing'}</span>
                      </Link>
                      <Link
                        to="/profile"
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        <span>{t('navigation.profileSettings')}</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                      >
                        <LogOut className="w-4 h-4" />
                        <span>{t('navigation.logout')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </nav>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
                <LanguageToggle className="w-full justify-center mb-2" />
                <div className="px-4 py-4 bg-gray-50 rounded-xl mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center text-white font-semibold">
                      {user?.username?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{user?.username}</p>
                      <p className="text-xs text-gray-500">{user?.email || 'User Account'}</p>
                    </div>
                  </div>
                </div>
                <Link 
                  to="/" 
                  className="px-4 py-3 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  خانه
                </Link>
                <Link 
                  to="/wallet" 
                  className="px-4 py-3 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('wallet.title') || 'Wallet & Billing'}
                </Link>
                <Link 
                  to="/profile" 
                  className="px-4 py-3 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navigation.profileSettings')}
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="px-4 py-3 text-center text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                >
                  {t('navigation.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-20"></div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 w-full">
        {/* Page Header */}
        <div className="mb-8 md:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 md:mb-3">
            {t('dashboard.title')}
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">{t('dashboard.loading')}</p>
            </div>
          </div>
        ) : chatbots.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Bot className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
              {t('dashboard.noBotsMessage')}
            </h3>
            <p className="text-gray-600 mb-8">
              Create your first chatbot to get started
            </p>
            <Link 
              to="/create"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl transition-all"
            >
              <Plus className="w-5 h-5" />
              <span>{t('dashboard.createFirst')}</span>
            </Link>
          </div>
        ) : (
          /* Chatbots Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chatbots.map((bot) => (
              <div key={bot.slug} className="group bg-white rounded-2xl border border-gray-200 hover:border-gray-300 transition-all overflow-hidden">
                {/* Card Header */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center text-white text-xl">
                      <Bot className="w-6 h-6" />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/edit/${bot.slug}`)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(bot.slug)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                  
                  <h3 className={`text-lg font-semibold text-gray-900 mb-2 line-clamp-1 ${bot.language === 'fa' ? 'text-right' : 'text-left'}`} dir={bot.language === 'fa' ? 'rtl' : 'ltr'}>
                    {bot.name}
                  </h3>
                  {bot.description && (
                    <p className={`text-sm text-gray-600 line-clamp-2 ${bot.language === 'fa' ? 'text-right' : 'text-left'}`} dir={bot.language === 'fa' ? 'rtl' : 'ltr'}>
                      {bot.description}
                    </p>
                  )}
                </div>

                {/* Card Footer */}
                <div className="p-6 bg-gray-50">
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(bot.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageSquare className="w-4 h-4" />
                      <span>{bot.template_type}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <a
                      href={getPublicChatbotUrl(bot.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>View</span>
                    </a>
                    <button
                      onClick={() => copyToClipboard(getPublicChatbotUrl(bot.slug))}
                      className="px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl transition-all"
                      title="Copy link"
                    >
                      <Copy className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                {t('dashboard.delete.title')}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                {t('dashboard.delete.message')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-xl transition-all text-sm sm:text-base"
                >
                  {t('dashboard.delete.cancel')}
                </button>
                <button
                  onClick={() => deleteChatbot(showDeleteConfirm)}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all text-sm sm:text-base"
                >
                  {t('dashboard.delete.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-4 px-4 sm:px-6 lg:px-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Bot className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm font-bold text-gray-900">{t('app.name')}</span>
            </div>
            <p className="text-xs text-gray-600">
              © 2025 {t('app.name')} - MSF Company. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Plus Button for Creating New Bot */}
      <Link
        to="/create"
        className={`fixed bottom-20 sm:bottom-24 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white rounded-full shadow-2xl shadow-gray-500/50 flex items-center justify-center transition-all hover:scale-110 z-50 group ${
          isRTL ? 'left-4 sm:left-8' : 'right-4 sm:right-8'
        }`}
        title={language === 'fa' ? 'ساخت ربات جدید' : 'Create New Bot'}
      >
        <Plus className="w-7 h-7 sm:w-8 sm:h-8 group-hover:rotate-90 transition-transform duration-300" />
      </Link>
    </div>
  );
};

export default Dashboard;
